/**
 * API методы для работы с сообщениями: список бесед, сообщения пациента, отправка
 * Получение информации о пациенте и привязках мессенджеров
 * Дата: 24.01.2026 - Добавлено логирование messenger операций
 */
import { apiClient } from './client';
import { MessageDTO, ConversationDTO } from './types';
import { logger } from '@/utils/logger';

export const messagesApi = {
  // Дата: 28.12.2025 - Добавлена поддержка tenantId для SuperAdmin (TODO-016)
  // Дата: 28.12.2025 - Добавлена фильтрация по каналу для TODO-018
  // Дата: 08.01.2026 - Добавлена пагинация с offset/limit для infinite scroll
  // Дата: 27.01.2026 - Добавлен search для поиска по телефону, ФИО, TG/MAX ID
  // Дата: 12.02.2026 - Добавлен параметр chatType для фильтрации личных/групповых чатов
  getConversations: async (tenantId?: string, channel?: string, offset?: number, limit?: number, search?: string, chatType?: string): Promise<{ conversations: ConversationDTO[]; total: number }> => {
    const params: Record<string, string> = {};
    if (tenantId) params.tenant_id = tenantId;
    if (channel && channel !== 'all') params.channel = channel;
    if (offset !== undefined) params.offset = String(offset);
    if (limit !== undefined) params.limit = String(limit);
    if (search) params.search = search;
    if (chatType && chatType !== 'all') params.chat_type = chatType;
    const response = await apiClient.get('/conversations', { params });
    return response.data;
  },

  // Дата: 28.12.2025 - Исправлено: изменен путь на /messages/{phone} (соответствие backend)
  // Дата: 10.01.2026 - Исправлено: encodeURIComponent для phone с двоеточием (tg:USER_ID)
  getConversationMessages: async (
    phone: string,
    channel?: string,
    tenantId?: string,
    limit?: number,
    offset?: number
  ): Promise<{ messages: MessageDTO[]; total: number }> => {
    const params: Record<string, string> = {};
    if (channel) params.channel = channel;
    if (tenantId) params.tenant_id = tenantId;
    if (limit !== undefined) params.limit = String(limit);
    if (offset !== undefined) params.offset = String(offset);
    const response = await apiClient.get(`/messages/${encodeURIComponent(phone)}`, { params });
    return response.data;
  },

  // Дата: 28.12.2025 - Исправлено: используем правильный endpoint /messages/send
  // Дата: 10.01.2026 - Добавлен tenantId для SuperAdmin
  // Дата: 24.01.2026 - Добавлено логирование отправки сообщений
  sendMessage: async (data: {
    phone: string;
    channel: string;
    content: string;
    content_type: string;
    tenant_id?: string;
    ai_source?: 'ai_clean' | 'ai_edited' | null;
    ai_suggestion_id?: string | null;
    ai_event_id?: string | null;
  }): Promise<MessageDTO> => {
    const params: Record<string, string> = {};
    if (data.tenant_id) params.tenant_id = data.tenant_id;
    
    // Логируем попытку отправки
    logger.info('message_send_attempt', {
      channel: data.channel,
      content_type: data.content_type,
      tenant_id: data.tenant_id,
      // НЕ логируем phone и content (PII)
    });
    
    try {
      const response = await apiClient.post('/messages/send', data, { params });
      
      // Логируем успешную отправку
      logger.info('message_send_success', {
        channel: data.channel,
        message_id: response.data?.id,
        tenant_id: data.tenant_id,
      });
      
      return response.data;
    } catch (error: unknown) {
      // Логируем ошибку отправки
      const axiosError = error as { response?: { status?: number; data?: { error?: string } }; message?: string };
      logger.error('message_send_failed', {
        channel: data.channel,
        tenant_id: data.tenant_id,
        status: axiosError.response?.status,
        error_message: axiosError.response?.data?.error || axiosError.message,
      });
      throw error;
    }
  },

  // Дата: 10.01.2026 - encodeURIComponent для phone, добавлен tenantId
  getPatientInfo: async (phone: string, tenantId?: string) => {
    const params: Record<string, string> = {};
    if (tenantId) params.tenant_id = tenantId;
    const response = await apiClient.get(`/patients/${encodeURIComponent(phone)}`, { params });
    return response.data;
  },

  // Дата: 10.01.2026 - encodeURIComponent для phone, добавлен tenantId
  getPatientBindings: async (phone: string, tenantId?: string): Promise<{ bindings: any[] }> => {
    const params: Record<string, string> = {};
    if (tenantId) params.tenant_id = tenantId;
    const response = await apiClient.get(`/linking/${encodeURIComponent(phone)}`, { params });
    return response.data;
  },

  // Дата: 09.01.2026 - TODO-019a: Получение fallback цепочки для сообщения
  getFallbackChain: async (messageId: string, tenantId?: string): Promise<{ message_id: string; chain: MessageDTO[]; count: number }> => {
    const params: Record<string, string> = {};
    if (tenantId) params.tenant_id = tenantId;
    const response = await apiClient.get(`/delivery/fallback/${messageId}/chain`, { params });
    return response.data;
  },

  // Дата: 26.01.2026 - Отметить сообщения прочитанными (по каналу или все)
  // channel: "waba", "regular", "telegram_bot", "telegram_client", "max" или undefined для всех
  markAsRead: async (phone: string, tenantId?: string, channel?: string): Promise<void> => {
    const params: Record<string, string> = {};
    if (tenantId) params.tenant_id = tenantId;
    if (channel) params.channel = channel;
    
    logger.info('mark_as_read', {
      channel: channel || 'all',
      tenant_id: tenantId,
    });
    
    await apiClient.post(`/messages/${encodeURIComponent(phone)}/read`, null, { params });
  },

  // Дата: 31.01.2026 - TODO-051: Проверка существования переписки
  // Дата: 01.02.2026 - Добавлен маппинг whatsapp для regular, исправлен комментарий
  // Проверяет, есть ли уже сообщения для данного телефона и канала
  checkConversationExists: async (
    phone: string,
    channel: string,
    tenantId?: string
  ): Promise<boolean> => {
    try {
      // Маппинг frontend channel ID на backend channel для getConversationMessages
      // Бэкенд GetConversation принимает channel напрямую из query параметра
      const channelMap: Record<string, string> = {
        whatsapp_waba: 'whatsapp_waba',
        whatsapp_regular: 'whatsapp', // Regular хранится как "whatsapp" в БД
        telegram_bot: 'telegram', // В БД telegram_bot хранится как "telegram"
        telegram_client: 'telegram_client',
        max: 'max',
        max_client: 'max_client',
        sms: 'sms',
      };
      
      const backendChannel = channelMap[channel] || channel;
      const response = await messagesApi.getConversationMessages(phone, backendChannel, tenantId);
      return response.total > 0;
    } catch (error) {
      // Если ошибка (например, 404), значит переписки нет
      return false;
    }
  },
};

